import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useRef, useState, useSyncExternalStore } from "react";
import { useRouter } from "next/router";
import type { Phase } from "@remi/enums/phase";
import { PhaseDisplayNames } from "@remi/enums/phase";
import { getError } from "@remi/error";
import { remiLogger } from "@remi/logging";
import { RemiLink, useEffect } from "@remi/next-ui";
import { can } from "@remi/permissions";
import { assertNever } from "@remi/ts-utils";
import type { CacheMessageForType } from "@remi/types/socket/message";
import { CacheMessageType } from "@remi/types/socket/message";
import { TaskDisplay } from "@remi/types/tasks/task-display-settings";

import { api } from "~/utils/api";
import { isKnownTaskType } from "~/utils/task-display-name";
import { displayToast } from "~/components/ui/sonner";
import {
  COMPLETION_UNCONFIRMED,
  type CompletionTerminalKind,
  type CompletionTrackerPhase,
  classifyCompletionMessage,
  getScopesToRun,
  getTerminalDisposition,
  INVALIDATION_COALESCE_WINDOW_MS,
  type InvalidationScopes,
  isCompletionPending,
  mergeInvalidationScopes,
  PHASE_RANK,
  shouldCoalesceInvalidation,
  shouldRunNavigationIntent,
} from "~/contexts/completionTrackerRules";
import { DetachableChannelProvider } from "~/contexts/DetachableChannelProvider";
import { useCacheChannel } from "~/contexts/hooks/useCacheChannel";
import { useUserProfile } from "~/contexts/UserProfileContext";
import { getCacheChannelName } from "~/contexts/utils/cache";
import { invalidateCompletionScopes } from "~/hooks/completionInvalidation";

const COMPLETION_ENTRY_TTL_MS = 300_000;

const COMPLETION_SETTLE_TIMEOUT_MS = 60_000;

const COMPLETION_TOAST_DURATION_MS = 10_000;

const TASK_LIST_PATH = "/dashboard/tasks";

const captureRejection = (work: Promise<void>): void => {
  void work.catch((error: unknown) => {
    remiLogger.captureError(getError(error));
  });
};

interface CompletionTrackerEntry {
  completionNonce: string;
  taskId: string;
  projectId: number;
  submittedAt: number;
  phase: CompletionTrackerPhase;
  navigateOnEvaluated: NavigationIntent | null;
  // The durable path has no newTasks/newPhase in its response, so both toasts are derived from a
  // diff against what the project held when the completion was submitted. A promise so a cold
  // cache never holds the mutation back.
  knownTaskIds: Promise<readonly string[] | null>;
  previousPhase: Phase | null;
}

interface NavigationIntent {
  registeredPathname: string;
  navigate: () => void;
}

interface RegisterCompletionInput {
  completionNonce: string;
  taskId: string;
  projectId: number;
  knownTaskIds: Promise<readonly string[] | null>;
  previousPhase: Phase | null;
}

interface CompletionTrackerContextValue {
  register: (input: RegisterCompletionInput) => void;
  confirmSubmitted: (completionNonce: string) => void;
  discard: (completionNonce: string) => void;
  registerNavigationIntent: (completionNonce: string, intent: NavigationIntent) => void;
  subscribe: (taskId: string, onStoreChange: () => void) => () => void;
  getEntriesForTask: (taskId: string) => readonly CompletionTrackerEntry[];
}

const EMPTY_ENTRIES: readonly CompletionTrackerEntry[] = Object.freeze([]);

const NO_TRACKER: CompletionTrackerContextValue = {
  register: () => undefined,
  confirmSubmitted: () => undefined,
  discard: () => undefined,
  registerNavigationIntent: () => undefined,
  subscribe: () => () => undefined,
  getEntriesForTask: () => EMPTY_ENTRIES,
};

const CompletionTrackerContext = createContext<CompletionTrackerContextValue>(NO_TRACKER);

export const useCompletionTracker = (): CompletionTrackerContextValue => useContext(CompletionTrackerContext);

const getServerSnapshot = (): boolean => false;

export const useTaskCompletionPending = (taskId: string): boolean => {
  const { subscribe, getEntriesForTask } = useCompletionTracker();
  const subscribeToTask = useCallback(
    (onStoreChange: () => void) => subscribe(taskId, onStoreChange),
    [subscribe, taskId],
  );
  const getSnapshot = useCallback(() => isCompletionPending(getEntriesForTask(taskId)), [getEntriesForTask, taskId]);
  return useSyncExternalStore(subscribeToTask, getSnapshot, getServerSnapshot);
};

interface EntryTimers {
  settle: ReturnType<typeof setTimeout> | null;
  ttl: ReturnType<typeof setTimeout> | null;
}

interface PendingInvalidation {
  taskIds: Set<string>;
  scopes: InvalidationScopes;
  lastInvalidatedTaskIds: ReadonlySet<string>;
  lastInvalidatedScopes: InvalidationScopes;
  lastRunAt: number;
  trailing: ReturnType<typeof setTimeout> | null;
}

interface TrackerStore {
  entries: Map<string, CompletionTrackerEntry>;
  timers: Map<string, EntryTimers>;
  byTask: Map<string, readonly CompletionTrackerEntry[]>;
  listeners: Map<string, Set<() => void>>;
  pendingInvalidations: Map<number, PendingInvalidation>;
}

const createStore = (): TrackerStore => ({
  entries: new Map(),
  timers: new Map(),
  byTask: new Map(),
  listeners: new Map(),
  pendingInvalidations: new Map(),
});

const COMPLETION_CHANNEL_OPTIONS = { params: { rewind: "1" } };

export const CompletionTrackerProvider = ({ children }: PropsWithChildren) => {
  const utils = api.useUtils();
  const router = useRouter();
  const { user } = useUserProfile();
  const userRef = useRef(user);
  userRef.current = user;
  // Read through refs, not the closure: the context value is built once so consumers never re-render.
  const utilsRef = useRef(utils);
  utilsRef.current = utils;
  const pathnameRef = useRef(router.asPath);
  pathnameRef.current = router.asPath;
  const routerRef = useRef(router);
  routerRef.current = router;

  const storeRef = useRef<TrackerStore | null>(null);
  storeRef.current ??= createStore();
  const store = storeRef.current;

  const [projectIds, setProjectIds] = useState<readonly number[]>([]);

  const contextRef = useRef<CompletionTrackerContextValue | null>(null);
  const dispatchRef = useRef<
    ((message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>) => Promise<void>) | null
  >(null);
  const onChannelMessage = useRef((message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>) => {
    const dispatch = dispatchRef.current;
    if (dispatch === null) {
      return;
    }
    captureRejection(dispatch(message));
  }).current;

  if (contextRef.current === null) {
    const publish = (taskId: string) => {
      const next = Array.from(store.entries.values()).filter((entry) => entry.taskId === taskId);
      if (next.length === 0) {
        store.byTask.delete(taskId);
      } else {
        store.byTask.set(taskId, next);
      }
      for (const listener of store.listeners.get(taskId) ?? []) {
        listener();
      }
    };

    const syncProjectIds = () => {
      const live = new Set(Array.from(store.entries.values()).map((entry) => entry.projectId));
      setProjectIds((current) =>
        current.length === live.size && current.every((id) => live.has(id)) ? current : Array.from(live),
      );
    };

    const clearTimers = (completionNonce: string) => {
      const timers = store.timers.get(completionNonce);
      if (timers === undefined) {
        return;
      }
      for (const timer of [timers.settle, timers.ttl]) {
        if (timer !== null) {
          clearTimeout(timer);
        }
      }
      store.timers.delete(completionNonce);
    };

    const removeEntry = (completionNonce: string) => {
      const entry = store.entries.get(completionNonce);
      if (entry === undefined) {
        return;
      }
      clearTimers(completionNonce);
      store.entries.delete(completionNonce);
      publish(entry.taskId);
      syncProjectIds();
    };

    const writeEntry = (entry: CompletionTrackerEntry) => {
      store.entries.set(entry.completionNonce, entry);
      publish(entry.taskId);
    };

    const transition = (entry: CompletionTrackerEntry, next: CompletionTrackerPhase): boolean => {
      if (PHASE_RANK[next] <= PHASE_RANK[entry.phase]) {
        return false;
      }
      writeEntry({ ...entry, phase: next });
      return true;
    };

    const flushInvalidation = async (projectId: number): Promise<void> => {
      const pending = store.pendingInvalidations.get(projectId);
      if (pending === undefined || pending.taskIds.size === 0) {
        return;
      }
      const taskIds = Array.from(pending.taskIds);
      const scopes = pending.scopes;
      pending.taskIds.clear();
      pending.scopes = [];
      const isTrailingFlush = pending.trailing !== null;
      if (pending.trailing !== null) {
        clearTimeout(pending.trailing);
      }
      pending.trailing = null;
      pending.lastRunAt = Date.now();
      pending.lastInvalidatedTaskIds = isTrailingFlush
        ? new Set([...pending.lastInvalidatedTaskIds, ...taskIds])
        : new Set(taskIds);
      pending.lastInvalidatedScopes = isTrailingFlush
        ? mergeInvalidationScopes(pending.lastInvalidatedScopes, scopes)
        : scopes;
      await invalidateCompletionScopes(utilsRef.current, { taskIds, projectId }, scopes);
    };

    const invalidateForCompletion = async (
      entry: CompletionTrackerEntry,
      requested: InvalidationScopes,
    ): Promise<void> => {
      const now = Date.now();
      const pending = store.pendingInvalidations.get(entry.projectId) ?? {
        taskIds: new Set<string>(),
        scopes: [],
        lastInvalidatedTaskIds: new Set<string>(),
        lastInvalidatedScopes: [],
        lastRunAt: 0,
        trailing: null,
      };
      store.pendingInvalidations.set(entry.projectId, pending);

      if (!shouldCoalesceInvalidation(pending.lastRunAt === 0 ? undefined : pending.lastRunAt, now)) {
        pending.taskIds.add(entry.taskId);
        pending.scopes = mergeInvalidationScopes(pending.scopes, requested);
        await flushInvalidation(entry.projectId);
        return;
      }
      const scopesToRun = getScopesToRun(pending.lastInvalidatedScopes, requested);
      if (scopesToRun?.length === 0 && pending.lastInvalidatedTaskIds.has(entry.taskId)) {
        return;
      }
      pending.taskIds.add(entry.taskId);
      pending.scopes = mergeInvalidationScopes(pending.scopes, scopesToRun);
      if (pending.trailing !== null) {
        return;
      }
      const delay = Math.max(0, pending.lastRunAt + INVALIDATION_COALESCE_WINDOW_MS - now);
      pending.trailing = setTimeout(() => captureRejection(flushInvalidation(entry.projectId)), delay);
    };

    const runNavigationIntent = (entry: CompletionTrackerEntry) => {
      const intent = entry.navigateOnEvaluated;
      if (intent === null) {
        return;
      }
      if (!shouldRunNavigationIntent(intent.registeredPathname, pathnameRef.current)) {
        remiLogger.info("task.completion.client.navigation-dropped", {
          taskId: entry.taskId,
          completionNonce: entry.completionNonce,
        });
        return;
      }
      intent.navigate();
    };

    const handleCommitted = async (entry: CompletionTrackerEntry): Promise<void> => {
      if (!transition(entry, "committed")) {
        return;
      }
      remiLogger.info("task.completion.client.committed", {
        taskId: entry.taskId,
        completionNonce: entry.completionNonce,
        latencyMs: Date.now() - entry.submittedAt,
      });
      await invalidateForCompletion(entry, []);
    };

    const toastSpawnedTasks = (entry: CompletionTrackerEntry) => {
      void (async () => {
        const knownTaskIds = await entry.knownTaskIds;
        if (knownTaskIds === null) {
          return;
        }
        try {
          const tasks = await utilsRef.current.task.getByProjectId.fetch({ projectId: entry.projectId });
          const known = new Set(knownTaskIds);
          for (const task of tasks) {
            if (known.has(task.id) || !isKnownTaskType(task.type) || !can.task.read(userRef.current, task)) {
              continue;
            }
            displayToast({
              title: "New task created",
              description: `${TaskDisplay[task.type].name} was created`,
              variant: "secondary",
              link: (
                <RemiLink href={`/dashboard/tasks/${task.id}`} variant={"accent"}>
                  View task
                </RemiLink>
              ),
            });
          }
        } catch (error) {
          remiLogger.warn("Failed to derive the new-task toast from the refetched task list.", {
            projectId: entry.projectId,
            errorMessage: error instanceof Error ? error.message : String(error),
          });
        }
      })();
    };

    const toastPhaseUpdate = (entry: CompletionTrackerEntry) => {
      void (async () => {
        try {
          const project = await utilsRef.current.project.byId.fetch(entry.projectId);
          if (project.currentPhase === entry.previousPhase) {
            return;
          }
          const phaseItem = project.bidId ? "Bid" : "Project";
          displayToast({
            title: `${phaseItem} phase updated`,
            description: `${phaseItem} is now in phase ${PhaseDisplayNames[project.currentPhase]}`,
            variant: "info",
          });
        } catch (error) {
          remiLogger.warn("Failed to derive the phase-updated toast from the refetched project.", {
            projectId: entry.projectId,
            errorMessage: error instanceof Error ? error.message : String(error),
          });
        }
      })();
    };

    const handleEvaluated = async (entry: CompletionTrackerEntry): Promise<void> => {
      if (!transition(entry, "evaluated")) {
        return;
      }
      remiLogger.info("task.completion.client.evaluated", {
        taskId: entry.taskId,
        completionNonce: entry.completionNonce,
        latencyMs: Date.now() - entry.submittedAt,
      });
      await invalidateForCompletion(entry, undefined);
      toastSpawnedTasks(entry);
      toastPhaseUpdate(entry);
      runNavigationIntent({ ...entry, phase: "evaluated" });
      removeEntry(entry.completionNonce);
    };

    const handleTerminal = async (entry: CompletionTrackerEntry, kind: CompletionTerminalKind): Promise<void> => {
      const disposition = getTerminalDisposition(entry.phase, kind);
      remiLogger.warn("task.completion.client.terminal", {
        taskId: entry.taskId,
        completionNonce: entry.completionNonce,
        kind,
        observedPhase: entry.phase,
        reverted: disposition.revert,
      });
      if (disposition.revert) {
        await revertOptimisticCompletion(entry);
      }
      await invalidateForCompletion(entry, undefined);
      if (disposition.toast !== null) {
        displayToast({ ...disposition.toast, duration: COMPLETION_TOAST_DURATION_MS });
      }
      removeEntry(entry.completionNonce);
    };

    // Invalidate, never replay a snapshot: a terminal can land after other refetches brought in fresher
    // data, and restoring a stale snapshot over it would clobber them.
    const revertOptimisticCompletion = async (entry: CompletionTrackerEntry): Promise<void> => {
      await Promise.allSettled([
        utilsRef.current.task.getTaskById.invalidate(entry.taskId),
        utilsRef.current.project.byId.invalidate(entry.projectId),
      ]);
    };

    const onSettleTimeout = async (completionNonce: string): Promise<void> => {
      const entry = store.entries.get(completionNonce);
      if (entry === undefined) {
        return;
      }
      const timers = store.timers.get(completionNonce);
      if (timers !== undefined) {
        timers.settle = null;
      }
      if (PHASE_RANK[entry.phase] >= PHASE_RANK.evaluated) {
        return;
      }
      // Committed may already outrank delayed by the time this fires — that only means the
      // transition is a no-op, not that the fallback below should be skipped.
      transition(entry, "delayed");
      remiLogger.warn("task.completion.client.settle-timed-out", { taskId: entry.taskId, completionNonce });
      await invalidateForCompletion(entry, undefined);
      runNavigationFallback(completionNonce);
    };

    // A lost or late `evaluated` would otherwise leave a user who asked to be taken to the spawned task
    // sitting where they were, with no signal. The spawned task's id is unknowable at this point, so the
    // fallback is the task list, and the intent's own staleness guard still applies.
    const runNavigationFallback = (completionNonce: string) => {
      const entry = store.entries.get(completionNonce);
      const intent = entry?.navigateOnEvaluated;
      if (entry === undefined || intent === undefined || intent === null) {
        return;
      }
      writeEntry({ ...entry, navigateOnEvaluated: null });
      if (!shouldRunNavigationIntent(intent.registeredPathname, pathnameRef.current)) {
        remiLogger.info("task.completion.client.navigation-dropped", {
          taskId: entry.taskId,
          completionNonce,
          reason: "settle-timeout-stale",
        });
        return;
      }
      remiLogger.info("task.completion.client.navigation-fallback", { taskId: entry.taskId, completionNonce });
      void routerRef.current.push(TASK_LIST_PATH);
    };

    const onTtl = async (completionNonce: string): Promise<void> => {
      const entry = store.entries.get(completionNonce);
      if (entry === undefined) {
        return;
      }
      const observedCommitted = PHASE_RANK[entry.phase] >= PHASE_RANK.committed;
      await invalidateForCompletion(entry, undefined);
      remiLogger.warn("task.completion.client.expired", {
        taskId: entry.taskId,
        completionNonce,
        lastObservedPhase: entry.phase,
      });
      if (!observedCommitted) {
        displayToast({ title: COMPLETION_UNCONFIRMED, variant: "error", duration: COMPLETION_TOAST_DURATION_MS });
      }
      removeEntry(completionNonce);
    };

    const handleMessage = async (
      message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>,
    ): Promise<void> => {
      const completionNonce = message.completionNonce;
      if (completionNonce === undefined) {
        return;
      }
      const entry = store.entries.get(completionNonce);
      if (entry === undefined) {
        return;
      }
      const classification = classifyCompletionMessage(message);
      switch (classification.kind) {
        case "ordinary":
          return;
        case "committed":
          await handleCommitted(entry);
          return;
        case "evaluated":
          await handleEvaluated(entry);
          return;
        case "failed":
        case "aborted":
        case "raceLost":
          await handleTerminal(entry, classification.kind);
          return;
        default:
          return assertNever(
            classification,
            `unhandled completion message classification: ${JSON.stringify(classification)}`,
          );
      }
    };

    dispatchRef.current = handleMessage;

    contextRef.current = {
      register: (input) => {
        writeEntry({
          ...input,
          submittedAt: Date.now(),
          phase: "pending",
          navigateOnEvaluated: null,
        });
        store.timers.set(input.completionNonce, {
          settle: null,
          // Armed at register, not at onSuccess: this is the backstop for every way a completion can
          // go silent, including a mutation that never resolves at all.
          ttl: setTimeout(() => captureRejection(onTtl(input.completionNonce)), COMPLETION_ENTRY_TTL_MS),
        });
        syncProjectIds();
      },
      confirmSubmitted: (completionNonce) => {
        const entry = store.entries.get(completionNonce);
        // Guard on rank, not on "pending" exactly: a `committed` publish that beats onSuccess must
        // still arm the settle timer, or the entry dead-ends silently at its 5-minute TTL.
        if (entry === undefined || PHASE_RANK[entry.phase] >= PHASE_RANK.evaluated) {
          return;
        }
        const timers = store.timers.get(completionNonce);
        if (timers === undefined || timers.settle !== null) {
          return;
        }
        timers.settle = setTimeout(
          () => captureRejection(onSettleTimeout(completionNonce)),
          COMPLETION_SETTLE_TIMEOUT_MS,
        );
      },
      discard: (completionNonce) => removeEntry(completionNonce),
      registerNavigationIntent: (completionNonce, intent) => {
        const entry = store.entries.get(completionNonce);
        if (entry === undefined) {
          return;
        }
        writeEntry({ ...entry, navigateOnEvaluated: intent });
      },
      subscribe: (taskId, onStoreChange) => {
        const listeners = store.listeners.get(taskId) ?? new Set<() => void>();
        listeners.add(onStoreChange);
        store.listeners.set(taskId, listeners);
        return () => {
          listeners.delete(onStoreChange);
          if (listeners.size === 0) {
            store.listeners.delete(taskId);
          }
        };
      },
      getEntriesForTask: (taskId) => store.byTask.get(taskId) ?? EMPTY_ENTRIES,
    };
  }

  useEffect(() => {
    return () => {
      for (const [, timers] of store.timers) {
        for (const timer of [timers.settle, timers.ttl]) {
          if (timer !== null) {
            clearTimeout(timer);
          }
        }
      }
      store.timers.clear();
      for (const [, pending] of store.pendingInvalidations) {
        if (pending.trailing !== null) {
          clearTimeout(pending.trailing);
        }
      }
      store.pendingInvalidations.clear();
      // Entries go with their timers: refs survive React's simulated unmount, so an entry left behind
      // would render a pending chip whose TTL and settle timers no longer exist.
      store.entries.clear();
      store.byTask.clear();
      store.listeners.clear();
    };
  }, [store]);

  return (
    <CompletionTrackerContext.Provider value={contextRef.current}>
      {children}
      {/* Siblings, never wrappers: wrapping {children} would remount the page subtree on every live
          project change. */}
      {projectIds.map((projectId) => (
        <CompletionChannelListener key={projectId} projectId={projectId} onMessage={onChannelMessage} />
      ))}
    </CompletionTrackerContext.Provider>
  );
};

interface CompletionChannelListenerProps {
  projectId: number;
  onMessage: (message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>) => void;
}

const CompletionChannelListener = ({ projectId, onMessage }: CompletionChannelListenerProps) => (
  <DetachableChannelProvider
    channelName={getCacheChannelName(CacheMessageType.PROJECT_TASKS, projectId)}
    options={COMPLETION_CHANNEL_OPTIONS}
  >
    <CompletionChannelSubscriber projectId={projectId} onMessage={onMessage} />
  </DetachableChannelProvider>
);

const CompletionChannelSubscriber = ({ projectId, onMessage }: CompletionChannelListenerProps) => {
  useCacheChannel({ entityType: CacheMessageType.PROJECT_TASKS, entityId: projectId }, onMessage);
  return null;
};
